import { Delete as DeleteIcon } from '@mui/icons-material';
import CropIcon from '@mui/icons-material/Crop';
import {
  alpha,
  Box,
  Button,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { useContext, useState } from 'react';
import { UploadImageModalContext } from './UploadImageModalContext';

const OVERLAY_ALPHA = 0.8;

const OverlayContainer = styled(Box)<{
  selected: boolean;
  deleteOverlayVisible: boolean;
}>`
  height: 100%;
  width: 100%;
  position: absolute;

  &:hover {
    background: ${({ theme, selected, deleteOverlayVisible }) =>
      !deleteOverlayVisible && selected
        ? 'none'
        : alpha(theme.palette.other.spaceCadet, OVERLAY_ALPHA)};
  }
`;

const Controls = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: ${({ theme }) =>
    alpha(theme.palette.other.spaceCadet, OVERLAY_ALPHA)};
`;

const DeleteConfirmationContainer = styled(Box)`
  height: 100%;
  background: ${({ theme }) => alpha(theme.palette.other.spaceCadet, 0.5)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1.5)};
  box-sizing: border-box;
`;

const DeleteConfirmationText = styled(Typography)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.common.white};
`;

export interface Props {
  selected: boolean;
  handleDelete: () => void;
}

export function PhotoControls({ handleDelete, selected }: Props) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [state, dispatch] = useContext(UploadImageModalContext);

  const toggleConfirmDelete = () => {
    setShowDeleteConfirmation((prev) => !prev);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(false);
    handleDelete();
  };

  const toggleCropImage = () =>
    dispatch({ type: 'TOGGLE_CROP_IMAGE', payload: true });

  // disable crop if there is no image or if there is an image url, which means we do not allow a second crop
  const disableCrop = !state.enableCrop;

  return (
    <OverlayContainer
      selected={selected}
      data-testid="photo-controls"
      deleteOverlayVisible={showDeleteConfirmation}
    >
      {selected && !showDeleteConfirmation && (
        <Controls>
          <Tooltip title="Crop">
            <IconButton
              disabled={disableCrop}
              onClick={toggleCropImage}
              aria-label="Crop"
              sx={{
                color: 'common.white',
              }}
            >
              <CropIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              onClick={toggleConfirmDelete}
              aria-label="Delete"
              sx={{
                color: 'common.white',
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Controls>
      )}
      {showDeleteConfirmation && (
        <DeleteConfirmationContainer>
          <DeleteConfirmationText variant="subtitle2">
            Delete this photo?
          </DeleteConfirmationText>
          <Stack direction="column" sx={{ gap: 1, justifyContent: 'center' }}>
            <Button
              onClick={toggleConfirmDelete}
              variant="outlined"
              color="utility"
              size="small"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteClick}
              variant="outlined"
              color="utility"
              size="small"
            >
              Delete
            </Button>
          </Stack>
        </DeleteConfirmationContainer>
      )}
    </OverlayContainer>
  );
}
